<template>
  <div class="relative flex w-full justify-center h-full">
    <input v-if="props.inputAddress" type="search" ref="addressInput" class="controls absolute mb-2 bottom-0 rounded-lg border-2 border-teal-400 p-2 z-20 w-9/12" :placeholder="$t('map.search_address')" />
    <div ref="mapContainer" class="flex flex-col card-css w-full"></div>
    <div v-if="!mapStore.mapControls.mapTypeControl" class="absolute bottom-48 right-10 hidden sm:flex flex-col items-center justify-center" :class="mapStore.mapType == 'satellite' ? 'px-2 bg-white rounded-full w-16 h-16 ' : 'w-16 h-16'" :aria-label="$t('map.locate_button')">
        <Icon
            :name="locate"
            :size="46"
            color="#343434"
            class="cursor-pointer"
            @click="mapStore.setCenter(mapStore.center); mapStore.setZoom(17)"
        />
    </div>
    <div v-if="!mapStore.mapControls.mapTypeControl" class="absolute bottom-24 right-10 hidden sm:flex flex-col items-center justify-center" :class="mapStore.mapType == 'satellite' ? 'px-2 bg-white rounded-full w-16 h-16 ' : 'w-16 h-16'" :aria-label="$t('map.switch_map_type')">
        <Icon
            :name="swap"
            :size="46"
            color="#343434"
            class="cursor-pointer"
            @click="mapStore.setMapType()"
        />
    </div>
    <div v-if="!mapStore.mapControls.mapTypeControl" class="absolute bottom-24 right-5 flex sm:hidden flex-col items-center justify-center" :class="mapStore.mapType == 'satellite' ? 'px-2 bg-white rounded-full w-12 h-12 ' : 'w-12 h-12'" :aria-label="$t('map.locate_button')">
        <Icon
            :name="locate"
            :size="30"
            color="#343434"
            class="cursor-pointer"
            @click="mapStore.setCenter(mapStore.center); mapStore.setZoom(17)"
        />
    </div>
    <div v-if="!mapStore.mapControls.mapTypeControl" 
      class="absolute bottom-10 right-5 flex sm:hidden flex-col items-center justify-center" 
      :class="mapStore.mapType == 'satellite' ? 'px-2 bg-white rounded-full w-12 h-12 ' : 'w-12 h-12'" :aria-label="$t('map.switch_map_type')">
        <Icon
            :name="swap"
            :size="30"
            color="#343434"
            class="cursor-pointer"
            @click="mapStore.setMapType()"
        />
    </div>
    <div v-if="mapStore.mapControls.itemToShow != null" class="absolute top-5 sm:top-10 right-5 sm:right-10 flex flex-col items-center justify-center w-3/12 sm:w-2/12">
      <n-select v-model:value="mapStore.itemSelected" :options="mapStore.mapControls.itemToShow"/>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { NSelect } from "naive-ui"
import { shallowRef, ref, watch, onUnmounted } from "vue"
import { Loader } from "@googlemaps/js-api-loader"
import { useMapStore } from '@/stores/map'
import { storeToRefs } from 'pinia'
import Icon from "@/components/common/Icon.vue"


const locate = "ion:locate"
const swap = "ion:swap-horizontal"
const mapStore = useMapStore()
const { mapConfig } = storeToRefs(mapStore)
const mapContainer = shallowRef<HTMLDivElement | null>(null)
const addressInput = ref(null);

interface Props {
  zoom?: number
  inputAddress?: boolean
  mapType?: string
  mapControls?: {
    clickableIcons?: boolean
    disableDefaultUI?: boolean
    zoomControl?: boolean
    mapTypeControl?: boolean
    scaleControl?: boolean
    streetViewControl?: boolean
    rotateControl?: boolean
    fullscreenControl?: boolean
    itemToShow?: null | Array<any>
  },
  controlPlayerPos?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  zoom: 17,
  inputAddress: false,
  controlPlayerPos: false
})

// Always request "places" so Loader options match every other map in the app.
// @googlemaps/js-api-loader throws if constructed twice with different libraries.
const loader = shallowRef(new Loader({
  apiKey: mapStore.apiKey,
  version: "weekly",
  libraries: ["places"],
}))

mapStore.setZoom(props.zoom)

const initializeMap = async () => {
  try {
    // First load the Google Maps JavaScript API
    await loader.value.load()
    
    const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary

    if (mapContainer.value) {
      const map = new Map(mapContainer.value, {
        center: mapConfig.value.center,
        zoom: mapConfig.value.zoom,
        mapId: mapConfig.value.mapId,
        mapTypeId: mapStore.mapType,
        gestureHandling: "greedy",
        ...mapStore.mapControls
      })

      map.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          mapStore.handleMapClick({
            lat: event.latLng.lat(),
            lng: event.latLng.lng()
          })
        }
      })

      if (props.inputAddress && addressInput.value) {
        const Places = await loader.value.importLibrary("places") as google.maps.PlacesLibrary;
        const defaultBounds = {
              north: map.getCenter()!.lat() + 0.1,
              south: map.getCenter()!.lat() - 0.1,
              east: map.getCenter()!.lng() + 0.1,
              west: map.getCenter()!.lng() - 0.1,
          };

        const options = {
              bounds: defaultBounds,
              types: [],
              fields: ["address_components", "geometry", "icon", "name"],
              strictBounds: false,
          };

        const autocomplete = new Places.Autocomplete(addressInput.value, options);

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (!place.geometry?.location) return
            mapStore.setCenter({
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            });
            mapStore.setZoom(18)
        });
      }

      mapStore.setMapInstance(map)
    }
  } catch (error) {
    console.error('Error loading Google Maps:', error)
  }
}

watch(() => mapContainer.value, (newVal) => {
  if (newVal) {
    requestAnimationFrame(() => initializeMap())
  }
}, { immediate: false })

let previousMarkers: Array<{ id: string | number }> = mapStore.markers.map(m => ({ id: m.id }))

// Marker Google instances are created by setMapInstance → initializeMarkers and by the watch below.
// Do not also create onMounted — that raced with initializeMarkers and left orphan AdvancedMarkers.

watch(
  () => [mapStore.markers.map(m => String(m.id)).join('|'), mapStore.mapInstance ? 1 : 0] as const,
  async () => {
    if (!mapStore.mapInstance) {
      previousMarkers = mapStore.markers.map(m => ({ id: m.id }))
      return
    }

    const newVal = mapStore.markers
    const currentIds = new Set(newVal.map(m => String(m.id)))
    const previousIds = new Set(previousMarkers.map(m => String(m.id)))

    for (const marker of newVal) {
      const idKey = String(marker.id)
      if (!previousIds.has(idKey) || !mapStore.markerInstances[marker.id]) {
        await mapStore.createMarkerInstance(marker)
      } else {
        await mapStore.updateMarkerInstance(marker.id, marker)
      }
    }

    for (const prev of previousMarkers) {
      if (!currentIds.has(String(prev.id))) {
        await mapStore.deleteMarkerInstance(prev.id)
      }
    }

    previousMarkers = newVal.map(m => ({ id: m.id }))
    if (props.controlPlayerPos) {
      mapStore.updatePlayerPosMap()
    }
  },
  {
    flush: 'post'
  }
)

onUnmounted(() => {
  mapStore.cleanup()
})
</script>

<style lang="scss" scoped>
.card-css {
  background-color: var(--bg-color);
}
</style>
